import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  Fade,
  IconButton,
  Tooltip
} from '@mui/material';
import { useIntl } from 'react-intl';
import DeleteIcon from '@mui/icons-material/Delete';
import { GridAddIcon } from '@mui/x-data-grid';

// Custom Components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDProgress from 'components/MDProgress';
import MDButton from 'components/MDButton';

// Services and Hooks
import {
  useCreateUsageLimitMutation,
  useDeleteUsageLimitMutation,
  useGetUsageLimitsQuery,
  useUpdateUsageLimitMutation
} from 'services/usagesLimitApi';
import { useNotification } from 'hook/useNotification';
import { useGetAssistantsQuery } from 'services/assistantApi';

// Utilities
import { getNativePeriodDates } from 'utils/dateUtils';

// Child Components
import UsageLimitDetails from 'pages/usages/limits/components/usageLimitDetails';

/**
 * Renders the Usage Limits management page, displaying a grid of usage limit cards,
 * allowing users to view progression, create, update, and delete limits.
 */
export default function UsageLimits() {
  const { formatMessage: msg } = useIntl();
  const { addNotification } = useNotification();

  // RTK Query hooks for data fetching and mutations
  const {
    data: allUsageLimits = [],
    isError: isFetchError,
    isLoading: isFetchingLimits,
    error: fetchError
  } = useGetUsageLimitsQuery();
  const [deleteUsageLimit] = useDeleteUsageLimitMutation();
  const [updateUsageLimit] = useUpdateUsageLimitMutation();
  const [createUsageLimit] = useCreateUsageLimitMutation();
  const { data: assistants = [], isLoading: isFetchingAssistants } = useGetAssistantsQuery();

  // State management for UI interactions
  const [openConfirmDeleteDialog, setOpenConfirmDeleteDialog] = useState(false);
  const [openConfirmDiscardDialog, setOpenConfirmDiscardDialog] = useState(false);
  const [selectedLimit, setSelectedLimit] = useState(null);
  const [isDetailDrawerOpen, setIsDetailDrawerOpen] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);

  // Combine loading states for a unified loading screen
  const overallLoading = isFetchingLimits || isFetchingAssistants;

  /**
   * Effect hook to display an error notification if fetching usage limits fails.
   */
  useEffect(() => {
    if (isFetchError) {
      addNotification({
        type: 'error',
        title: msg({ id: 'notification.error.title' }),
        message: msg({ id: 'page.usage.limits.loading.error' })
      });
    }
  }, [isFetchError, addNotification, msg]); // Dependencies: only re-run if these change

  const buildUsageLimitRequest = (data) => ({
    assistantId: data.assistantId,
    period: data.period,
    maxInputTokens: data.maxInputTokens || null,
    maxOutputTokens: data.maxOutputTokens || null,
    maxRequests: data.maxRequests || null,
    notificationMail: data.notificationMail?.trim() || ''
  });

  /**
   * Initiates the creation process for a new usage limit.
   */
  const handleCreateLimit = useCallback(
    async (data) => {
      try {
        await createUsageLimit({
          usageLimitRequest: {
            ...buildUsageLimitRequest(data)
          }
        }).unwrap();
        addNotification({
          type: 'success',
          title: msg({ id: 'notification.success.title' }),
          message: msg({ id: 'page.usage.limits.create.success' })
        });
      } catch (err) {
        console.error('Create error:', err);
        addNotification({
          type: 'error',
          title: msg({ id: 'notification.error.title' }),
          message:
            msg({ id: 'page.usage.limits.create.error' }) +
            `: ${err?.data?.message || err?.message || 'Unknown error'}`
        });
      }
    },
    [createUsageLimit, addNotification, msg]
  );

  /**
   * Initiates the update process for an existing usage limit.
   */
  const handleUpdateLimit = useCallback(
    async (data) => {
      try {
        await updateUsageLimit({
          id: data.id,
          usageLimitRequest: {
            id: data.id,
            ...buildUsageLimitRequest(data)
          }
        }).unwrap();
        addNotification({
          type: 'success',
          title: msg({ id: 'notification.success.title' }),
          message: msg({ id: 'page.usage.limits.update.success' })
        });
      } catch (err) {
        console.error('Update error:', err);
        addNotification({
          type: 'error',
          title: msg({ id: 'notification.error.title' }),
          message:
            msg({ id: 'page.usage.limits.update.error' }) +
            `: ${err?.data?.message || err?.message || 'Unknown error'}`
        });
      }
    },
    [updateUsageLimit, addNotification, msg]
  );

  /**
   * Closes the detail drawer. If the form is dirty, prompts for confirmation before closing.
   * @param {boolean} open - Desired state of the drawer (true for open, false for closed).
   */
  const handleToggleDrawer = useCallback(
    (open) => () => {
      if (isFormDirty) {
        setOpenConfirmDiscardDialog(true);
      } else {
        setIsDetailDrawerOpen(open);
        // Clear selectedLimit when closing drawer without saving
        if (!open) setSelectedLimit(null);
      }
    },
    [isFormDirty]
  );

  /**
   * Confirms closure of the drawer, discarding unsaved changes.
   */
  const handleConfirmDiscard = useCallback(() => {
    setIsDetailDrawerOpen(false);
    setOpenConfirmDiscardDialog(false);
    setIsFormDirty(false); // Reset dirty state
    setSelectedLimit(null); // Clear selected limit on discard
  }, []);

  /**
   * Cancels the discard confirmation, keeping the drawer open.
   */
  const handleCancelDiscard = useCallback(() => {
    setOpenConfirmDiscardDialog(false);
  }, []);

  /**
   * Handles the submission of the usage limit form. Delegates to create or update functions.
   * @param {object} data - The form data containing usage limit details.
   */
  const handleFormSubmit = useCallback(
    (data) => {
      if (data.id) {
        handleUpdateLimit(data);
      } else {
        handleCreateLimit(data);
      }
      setIsDetailDrawerOpen(false);
      setIsFormDirty(false); // Reset dirty state after successful submission
      setSelectedLimit(null); // Clear selected limit after submission
    },
    [handleUpdateLimit, handleCreateLimit] // Dependencies corrected: should include the functions themselves
  );

  /**
   * Sets the form's dirty state based on changes.
   * @param {boolean} dirty - True if the form has unsaved changes, false otherwise.
   */
  const handleFieldChange = useCallback((dirty) => {
    setIsFormDirty(dirty);
  }, []);

  /**
   * Opens the detail drawer for creating a new usage limit.
   */
  const handleAddNewLimit = useCallback(() => {
    setSelectedLimit(null); // Clear any previously selected limit
    setIsDetailDrawerOpen(true);
    setIsFormDirty(false); // New form is not dirty initially
  }, []);

  /**
   * Handles the deletion of a usage limit after confirmation.
   */
  const handleDeleteLimit = useCallback(
    async (id) => {
      setOpenConfirmDeleteDialog(false); // Close confirmation dialog immediately
      try {
        await deleteUsageLimit(id).unwrap();
        addNotification({
          type: 'success',
          title: msg({ id: 'notification.success.title' }),
          message: msg({ id: 'page.usage.limit.delete.success' })
        });
      } catch (error) {
        console.error(error);
        addNotification({
          type: 'error',
          title: msg({ id: 'notification.error.title' }),
          message: msg({ id: 'page.usage.limit.delete.error' })
        });
      }
    },
    [deleteUsageLimit, addNotification, msg]
  );

  /**
   * Calculates progress bar properties (value, color, label) based on current and maximum values.
   * Uses useMemo to memoize the function, preventing unnecessary re-creations.
   */
  const getProgressProps = useCallback(
    (current, max) => {
      if (max === null || max === 0) {
        return { value: 0, color: 'info', label: msg({ id: 'common.unlimited' }) };
      }
      const rawPercentage = (current / max) * 100;
      const roundedPercentage = Math.round(rawPercentage * 2) / 2; // Round to nearest 0.5

      let color = 'info';
      if (rawPercentage >= 90) {
        color = 'error';
      } else if (rawPercentage >= 75) {
        color = 'warning';
      }

      return {
        value: Math.min(roundedPercentage, 100), // Cap at 100%
        color: color,
        label: `${roundedPercentage}%`
      };
    },
    [msg]
  );

  // Memoize the sorted list of usage limits for performance
  const sortedUsageLimits = useMemo(() => {
    return [...allUsageLimits].sort((a, b) => a.usageLimit.id - b.usageLimit.id);
  }, [allUsageLimits]);

  // --- Render Loading/Error/No Data States ---
  if (overallLoading) {
    return (
      <MDBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{ height: 'calc(100vh - 200px)' }}
      >
        <MDTypography variant="h5">{msg({ id: 'common.progress' })}</MDTypography>
      </MDBox>
    );
  }

  if (isFetchError) {
    return (
      <MDBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{ height: 'calc(100vh - 200px)' }}
      >
        <MDTypography variant="h5" color="error">
          {msg({ id: 'page.usage.limits.loading.error' })}:{' '}
          {fetchError?.data || fetchError?.message || 'Unknown error'}
        </MDTypography>
      </MDBox>
    );
  }

  // --- Main Component Render ---
  return (
    <Fade in timeout={500}>
      <MDBox flex={1} display="flex" flexDirection="column" sx={{ height: '100%' }}>
        {sortedUsageLimits.length === 0 ? (
          <MDBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            sx={{ height: 'calc(100vh - 200px)' }}
          >
            <MDTypography variant="h5" mb={2}>
              {msg({ id: 'page.usage.limits.noData' })}
            </MDTypography>
            <MDButton variant="gradient" color="success" onClick={handleAddNewLimit}>
              {msg({ id: `action.create` })}
            </MDButton>
          </MDBox>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 1 }}>
            <Tooltip title={msg({ id: `action.create` })}>
              <IconButton
                aria-label="add"
                color="success"
                onClick={handleAddNewLimit}
                type="button"
                size="medium"
              >
                <GridAddIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        {/* Usage Limits Cards Grid Section */}
        <MDBox
          display="grid"
          gridTemplateColumns="repeat(auto-fill, minmax(300px, 1fr))"
          gap="10px"
          sx={{ overflowY: 'auto', p: 0.5, maxHeight: '75vh' }}
        >
          {sortedUsageLimits.map((dataItem) => {
            const limit = dataItem.usageLimit;
            const { currentInputTokens, currentOutputTokens, currentRequestCount } = dataItem;

            const inputTokensProgress = getProgressProps(currentInputTokens, limit.maxInputTokens);
            const outputTokensProgress = getProgressProps(
              currentOutputTokens,
              limit.maxOutputTokens
            );
            const requestsProgress = getProgressProps(currentRequestCount, limit.maxRequests);

            return (
              <Card
                key={`usage-limit-${limit.id}`} // More descriptive key
                elevation={3}
                sx={{ borderRadius: '10px', cursor: 'pointer' }}
                onClick={() => {
                  setSelectedLimit(limit);
                  setIsDetailDrawerOpen(true);
                }}
              >
                <CardHeader
                  title={
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <MDTypography variant="h6" fontWeight="medium" component="div">
                        {limit.assistant?.label ||
                          msg({ id: 'page.usage.limits.defaultAssistant' })}
                      </MDTypography>
                      <Tooltip title={msg({ id: 'tooltip.delete' })}>
                        <IconButton
                          edge="end"
                          size="small"
                          color="error"
                          aria-label="delete"
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent card onClick from firing
                            setSelectedLimit(limit);
                            setOpenConfirmDeleteDialog(true);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  }
                  subheader={
                    limit.period && (
                      <MDTypography variant="caption" color="textSecondary">
                        <span>Période du {getNativePeriodDates(limit.period)}</span>
                      </MDTypography>
                    )
                  }
                />

                <CardContent>
                  {/* Input Tokens Progress */}
                  {limit?.maxInputTokens != null && (
                    <Box sx={{ mb: 1 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <MDTypography variant="body2" color="textSecondary">
                          {msg({ id: 'page.usage.limits.inputTokens' })}
                        </MDTypography>
                        <MDTypography variant="body2" fontWeight="bold">
                          {currentInputTokens?.toLocaleString?.() || '0'}{' '}
                          {limit.maxInputTokens !== 0
                            ? `/ ${limit.maxInputTokens.toLocaleString()}`
                            : ''}
                        </MDTypography>
                      </Box>
                      <MDProgress
                        variant="gradient"
                        value={inputTokensProgress.value}
                        color={inputTokensProgress.color}
                        label={inputTokensProgress.label}
                        sx={{ height: 6, borderRadius: 5 }}
                      />
                    </Box>
                  )}

                  {/* Output Tokens Progress */}
                  {limit?.maxOutputTokens != null && (
                    <Box sx={{ mb: 1 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <MDTypography variant="body2" color="textSecondary">
                          {msg({ id: 'page.usage.limits.outputTokens' })}
                        </MDTypography>
                        <MDTypography variant="body2" fontWeight="bold">
                          {currentOutputTokens?.toLocaleString?.() || '0'}{' '}
                          {limit.maxOutputTokens !== 0
                            ? `/ ${limit.maxOutputTokens.toLocaleString()}`
                            : ''}
                        </MDTypography>
                      </Box>
                      <MDProgress
                        variant="gradient"
                        value={outputTokensProgress.value}
                        color={outputTokensProgress.color}
                        label={outputTokensProgress.label}
                        sx={{ height: 6, borderRadius: 5 }}
                      />
                    </Box>
                  )}

                  {/* Request Count Progress */}
                  {limit?.maxRequests != null && (
                    <Box sx={{ mb: 1 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <MDTypography variant="body2" color="textSecondary">
                          {msg({ id: 'page.usage.limits.requestsCount' })}
                        </MDTypography>
                        <MDTypography variant="body2" fontWeight="bold">
                          {currentRequestCount?.toLocaleString?.() || '0'}{' '}
                          {limit.maxRequests !== 0 ? `/ ${limit.maxRequests.toLocaleString()}` : ''}
                        </MDTypography>
                      </Box>
                      <MDProgress
                        variant="gradient"
                        value={requestsProgress.value}
                        color={requestsProgress.color}
                        label={requestsProgress.label}
                        sx={{ height: 6, borderRadius: 5 }}
                      />
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </MDBox>
        {/* Confirmation Dialog for Delete */}
        <Dialog open={openConfirmDeleteDialog} onClose={() => setOpenConfirmDeleteDialog(false)}>
          <DialogTitle>{msg({ id: 'page.usage.limit.delete.confirmation.title' })}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {msg({ id: 'page.usage.limit.delete.confirmation.message' })}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenConfirmDeleteDialog(false)} color="primary">
              {msg({ id: 'button.cancel' })}
            </Button>
            <Button onClick={() => handleDeleteLimit(selectedLimit.id)} color="error" autoFocus>
              {msg({ id: 'button.delete' })}
            </Button>
          </DialogActions>
        </Dialog>
        {/* Confirmation Dialog for Discarding Unsaved Changes */}
        <Dialog open={openConfirmDiscardDialog} onClose={handleCancelDiscard}>
          <DialogTitle>{msg({ id: 'dialog.unsaved_changes.title' })}</DialogTitle>
          <DialogContent>
            <DialogContentText>{msg({ id: 'dialog.unsaved_changes.message' })}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <MDButton onClick={handleCancelDiscard} color="secondary">
              {msg({ id: 'button.cancel' })}
            </MDButton>
            <MDButton onClick={handleConfirmDiscard} color="primary">
              {msg({ id: 'button.discard' })}
            </MDButton>
          </DialogActions>
        </Dialog>
        {/* Detail Drawer for Add/Edit Usage Limit */}
        <Drawer
          variant="temporary"
          anchor="right"
          open={isDetailDrawerOpen}
          onClose={handleToggleDrawer(false)} // Use the wrapped toggle
          ModalProps={{ keepMounted: false }}
          PaperProps={{
            sx: { width: 700, p: 2 }
          }}
        >
          {isDetailDrawerOpen && ( // Render only when open to ensure props are fresh
            <UsageLimitDetails
              usageLimit={selectedLimit}
              onSubmit={handleFormSubmit}
              onFieldChange={handleFieldChange}
              assistants={assistants}
            />
          )}
        </Drawer>
      </MDBox>
    </Fade>
  );
}
